/**
 * Cart Upsells (DEV-14331)
 *
 * <cart-recommendations> is the cart drawer's single upsell carousel, rendered by
 * snippets/cart-upsells.liquid for the source picked in Theme settings > Cart > Upsell source.
 * Every slide is a storefront-product shell (snippets/cart-upsell-product.liquid) that fetches the
 * cart-rec-card section with the variant best matching its parent cart line.
 *   - complementary / metafield: slides are rendered server-side into a <template>, already ordered
 *     newest cart line first, deduped and without in-cart products.
 *   - related: recommendations are only reachable from the Product Recommendations API, so slides are
 *     fetched per parent line (newest first) and ordered/deduped here.
 * Slides are only inserted once the drawer opens, so no card fetch runs on page load. Card forms are
 * submitted via AJAX so storefront-product's signed _discount_* properties reach the cart line.
 */

// ── Helper: extract inner HTML of a selector from a raw HTML string ───────
function getSectionInnerHTML(html, selector) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector(selector);
  return el ? el.innerHTML : null;
}

// ── Helper: re-render cart drawer sections after a cart mutation ─────────
async function refreshCartDrawer() {
  try {
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-count-bubble`);
    const state = await response.json();
    const drawerHtml = state['cart-drawer'];

    if (drawerHtml) {
      const drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const selectors = [
        '.drawer__inner > .drawer__header',
        'cart-drawer-items',
        '.drawer__footer',
        '.drawer__inner-empty-wrapper',
        '.cart-progress-bar__message',
      ];

      selectors.forEach((sel) => {
        const liveEl = document.querySelector(sel);
        const sourceEl = drawerDoc.querySelector(sel);
        if (!liveEl || !sourceEl) return;

        liveEl.innerHTML = sourceEl.innerHTML;
      });

      // Re-bind overlay close (mirrors CartDrawer.renderContents)
      const drawer = document.querySelector('cart-drawer');
      const overlay = document.getElementById('CartDrawer-Overlay');
      if (drawer && overlay) {
        overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }

    const countBubble = document.getElementById('cart-count-bubble');
    if (countBubble && state['cart-count-bubble']) {
      const newHtml = getSectionInnerHTML(state['cart-count-bubble'], '.shopify-section');
      if (newHtml !== null) countBubble.innerHTML = newHtml;
    }

    // Notify other cart subscribers (progress bar, etc.)
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations' });
    }
  } catch (_err) {
    // Silent — drawer still shows current state
  }
}

// ── Helper: AJAX add-to-cart from an upsell card's product form ───────────
// Posts the whole form so any signed _discount_* properties are kept.
async function addFormToCart(form, btn) {
  btn?.classList.add('loading');
  btn?.setAttribute('disabled', '');
  btn?.querySelector('.loading__spinner')?.classList.remove('hidden');

  try {
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
    const addResponse = await fetch(`${cartAddUrl}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });

    if (!addResponse.ok) {
      throw new Error(`Cart add failed: ${addResponse.status}`);
    }

    await refreshCartDrawer();

    // Ensure drawer stays open / re-opens
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !drawer.classList.contains('active')) {
      setTimeout(() => drawer.classList.add('animate', 'active'));
    }
  } catch (_err) {
    btn?.classList.remove('loading');
    btn?.removeAttribute('disabled');
    btn?.querySelector('.loading__spinner')?.classList.add('hidden');
  }
}

// ── CartRecommendations custom element ───────────────────────────────────
if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.onSubmit = this.onSubmit.bind(this);
      this.onCardError = this.onCardError.bind(this);
    }

    connectedCallback() {
      this.source = this.dataset.source;
      this.limit = parseInt(this.dataset.limit, 10) || 10;
      this.track = this.querySelector('.cart-recs__track');
      if (!this.track) return;

      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('storefront-product:error', this.onCardError);
      this.deferUntilDrawerOpen();
    }

    disconnectedCallback() {
      // Element is re-created on every cart-drawer re-render; clean up.
      this.drawerObserver?.disconnect();
      this.arrowsResizeObserver?.disconnect();
      this.arrowsItemsResizeObserver?.disconnect();
      this.trackMutationObserver?.disconnect();
      this._carouselAbort?.abort();
      this._carouselInit = false;
      this.updateArrows = undefined;
    }

    // Defer inserting slides (each one fetches its card) until the cart drawer is actually
    // opened, so nothing fires on initial page load (CWV-safe). The hook is the `active`
    // class CartDrawer.open() adds to <cart-drawer>. IntersectionObserver is unreliable
    // here because <cart-drawer> is position:fixed full-viewport.
    deferUntilDrawerOpen() {
      const drawer = this.closest('cart-drawer');

      // No drawer ancestor, or drawer already open (e.g. re-rendered after an
      // AJAX add-to-cart while open) → load immediately.
      if (!drawer || drawer.classList.contains('active')) {
        this.load();
        return;
      }

      this.drawerObserver = new MutationObserver(() => {
        if (drawer.classList.contains('active')) {
          this.drawerObserver.disconnect();
          this.load();
        }
      });
      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    async load() {
      // Guard against a double load (e.g. observer + immediate paths racing).
      if (this.hasLoaded) return;
      this.hasLoaded = true;

      if (this.source === 'related') {
        await this.appendRelatedSlides();
      } else {
        this.appendTemplateSlides();
      }

      // No fallback source: an empty carousel stays hidden.
      if (!this.track.children.length) return;

      this.hidden = false;
      this.initCarousel();
    }

    appendTemplateSlides() {
      const template = this.querySelector('template[data-upsell-cards]');
      // importNode so the storefront-product shells upgrade in this document.
      if (template) this.track.append(document.importNode(template.content, true));
    }

    async appendRelatedSlides() {
      const parents = this.parseJSONScript('upsell-parents') || [];
      const excludedIds = new Set((this.parseJSONScript('cart-product-ids') || []).map(String));
      const { url, sectionId } = this.dataset;
      if (!url || !sectionId) return;

      for (const parent of parents) {
        if (this.track.children.length >= this.limit) break;

        let doc;
        try {
          const response = await fetch(
            `${url}?section_id=${sectionId}&product_id=${parent.productId}&limit=${this.limit}`
          );
          if (!response.ok) continue;
          doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        } catch (_err) {
          continue;
        }

        for (const slide of doc.querySelectorAll('.cart-recs__item')) {
          if (this.track.children.length >= this.limit) break;

          const card = slide.querySelector('[is="storefront-product"]');
          const productId = card?.dataset.productId;
          if (!productId || excludedIds.has(productId)) continue;
          excludedIds.add(productId);

          // Variant matching against the cart line this recommendation came from.
          const parentOptions = card.querySelector('script[data-json-type="parent-current-options"]');
          if (parentOptions) parentOptions.textContent = JSON.stringify(parent.options || []);

          this.track.append(document.importNode(slide, true));
        }
      }
    }

    parseJSONScript(type) {
      const script = this.querySelector(`:scope > script[data-json-type="${type}"]`);
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (_err) {
        return null;
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action$="/cart/add"]')) return;

      event.preventDefault();
      addFormToCart(form, event.submitter || form.querySelector('[type="submit"]'));
    }

    // A card whose section fetch failed would otherwise stay a skeleton forever.
    onCardError(event) {
      event.target.closest('.cart-recs__item')?.remove();
      if (!this.track.children.length) this.hidden = true;
      this.updateArrows?.();
    }

    initCarousel() {
      if (this._carouselInit) return;

      const track = this.track;
      const prevBtn = this.querySelector('.cart-recs__prev');
      const nextBtn = this.querySelector('.cart-recs__next');
      const cartRecs = this.querySelector('.cart-recs');

      if (!prevBtn || !nextBtn) return;

      this._carouselInit = true;
      this._carouselAbort?.abort();
      this._carouselAbort = new AbortController();
      const { signal } = this._carouselAbort;

      // Arrows go to the same snap positions a finger swipe settles on (item start minus the
      // track's scroll-padding), rather than scrolling by a measured card width, so both land
      // the cards in exactly the same place.
      const getSnapPositions = () => {
        const trackLeft = track.getBoundingClientRect().left;
        const paddingStart = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
        const maxScroll = track.scrollWidth - track.clientWidth;
        return [...track.querySelectorAll('.cart-recs__item')].map((item) =>
          Math.min(
            maxScroll,
            Math.max(0, track.scrollLeft + item.getBoundingClientRect().left - trackLeft - paddingStart)
          )
        );
      };

      const scrollToAdjacent = (direction) => {
        const current = track.scrollLeft;
        const positions = getSnapPositions();
        const target =
          direction > 0
            ? positions.find((position) => position > current + 1)
            : positions.reverse().find((position) => position < current - 1);
        if (target !== undefined) track.scrollTo({ left: target, behavior: 'smooth' });
      };

      prevBtn.addEventListener('click', () => scrollToAdjacent(-1), { signal });
      nextBtn.addEventListener('click', () => scrollToAdjacent(1), { signal });

      const updateArrows = () => {
        const itemCount = track.querySelectorAll('.cart-recs__item').length;
        const isVerticalPair =
          cartRecs?.dataset.layout === 'vertical' &&
          itemCount === 2 &&
          track.scrollWidth <= track.clientWidth + 1;

        if (cartRecs) {
          if (isVerticalPair) cartRecs.dataset.fitsViewport = 'true';
          else delete cartRecs.dataset.fitsViewport;
        }

        // The last card can snap short of the furthest scroll position, so "at end" also means
        // the last snap position is reached; otherwise the next arrow stays enabled and does nothing.
        const lastSnap = getSnapPositions().pop() ?? 0;
        const atEnd = track.scrollLeft + track.offsetWidth >= track.scrollWidth - 1 || track.scrollLeft >= lastSnap - 1;
        prevBtn.disabled = track.scrollLeft <= 0;
        nextBtn.disabled = atEnd;

        const wrapper = track.closest('.cart-recs__track-wrapper');
        if (wrapper) wrapper.dataset.atEnd = atEnd;
      };

      this.updateArrows = updateArrows;
      track.addEventListener('scroll', updateArrows, { passive: true, signal });

      this.arrowsResizeObserver?.disconnect();
      this.arrowsResizeObserver = new ResizeObserver(() => updateArrows());
      this.arrowsResizeObserver.observe(track);

      const observeCarouselItems = () => {
        this.arrowsItemsResizeObserver?.disconnect();
        this.arrowsItemsResizeObserver = new ResizeObserver(() => updateArrows());
        track.querySelectorAll('.cart-recs__item').forEach((item) => {
          this.arrowsItemsResizeObserver.observe(item);
        });
      };

      observeCarouselItems();
      updateArrows();

      this.trackMutationObserver?.disconnect();
      this.trackMutationObserver = new MutationObserver(() => {
        observeCarouselItems();
        updateArrows();
      });
      this.trackMutationObserver.observe(track, { childList: true });
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
}
